import { Divider, Text, Badge, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { LineClamp } from '~/components/LineClamp/LineClamp';
import { CopyButton } from '~/components/CopyButton/CopyButton';
import { useMetadataCopy } from '~/hooks/useMetadataCopy';
import { trpc } from '~/utils/trpc';
import { LegacyActionIcon } from '~/components/LegacyActionIcon/LegacyActionIcon';

export function ImageMeta({ imageId }: { imageId: number }) {
  const { data } = trpc.image.getGenerationData.useQuery({ id: imageId });
  const { meta, onSite, process } = data ?? {};
  const { copy: copyAll, copied: copiedAll } = useMetadataCopy(meta);

  const simpleMeta = useMemo(() => {
    if (!data) return null;

    const meta: Record<string, any> = data.meta ?? {};
    if (!meta) return null;
    const { prompt, negativePrompt, ...restMeta } = meta;

    // On-site generations: the server resolves which keys are real generator
    // inputs via the generation graph (`displayKeys`). Off-site/foreign
    // metadata has no graph mapping, so show all of it.
    const candidateKeys =
      data.onSite && data.displayKeys ? data.displayKeys : Object.keys(restMeta);

    // The comfy workflow chip is display-only (not a graph input node) — always
    // surface it when present.
    const keySet = new Set(candidateKeys.filter((key) => key !== 'comfy'));
    if (restMeta.comfy) keySet.add('comfy');

    const keys = [...keySet].filter(
      (key) =>
        // prompt/negativePrompt render in their own sections, not as badges
        key !== 'prompt' &&
        key !== 'negativePrompt' &&
        // drop capitalized external-tool fields (A1111-style: Size, Model,
        // Schedule type, Unprompted, etc.)
        !/^[A-Z]/.test(key) &&
        meta[key] != null
    );

    function getSimpleMetaContent(key: string) {
      if (!meta) return null;
      switch (key) {
        case 'comfy':
          const comfy = meta['comfy'];
          const nodeCount: number = comfy?.workflow?.nodes?.length ?? 0;
          const nodeText =
            nodeCount === 1 ? `${nodeCount} Node` : `${nodeCount > 0 ? nodeCount : ''} Nodes`;

          return comfy ? (
            <CopyButton value={() => JSON.stringify(comfy.workflow)}>
              {({ copy, copied, Icon, color }) => (
                <UnstyledButton
                  className={clsx('flex items-center gap-1 rounded-lg', color && 'text-teal-500')}
                  onClick={copy}
                >
                  {!copied ? nodeText.trim() : 'Copied'}
                  <Icon size={14} />
                </UnstyledButton>
              )}
            </CopyButton>
          ) : null;
        default: {
          const content = meta[key];
          // omit boolean values from badges
          if (!content || typeof content === 'object' || typeof content === 'boolean') return null;
          return <span>{content}</span>;
        }
      }
    }

    return keys
      .map((key) => ({ label: key, content: getSimpleMetaContent(key) }))
      .filter(({ content }) => content !== null);
  }, [data]);

  if (!meta || !simpleMeta) return null;

  const software = meta.software ?? (onSite ? 'Civitai Generator' : 'External Generator');
  const { prompt, negativePrompt } = meta;
  const hasSimpleMeta = !!simpleMeta.length;

  return (
    <>
      {prompt ? (
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-1">
              <Text className="text-lg font-semibold">Prompt</Text>
              <Badge size="xs" radius="sm">
                {software}
              </Badge>
              <Badge size="xs" radius="sm">
                {process}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <CopyButton value={prompt}>
                {({ copy, copied, Icon, color }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy prompt'}>
                    <LegacyActionIcon onClick={copy} color={color}>
                      <Icon size={16} />
                    </LegacyActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <Tooltip label={copiedAll ? 'Copied' : 'Copy all metadata'}>
                <LegacyActionIcon onClick={copyAll} color={copiedAll ? 'teal' : undefined}>
                  {copiedAll ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </LegacyActionIcon>
              </Tooltip>
            </div>
          </div>
          <LineClamp c="dimmed" className="text-sm">
            {prompt}
          </LineClamp>
        </div>
      ) : (
        <div className="flex gap-1">
          <Badge size="xs" radius="sm">
            {software}
          </Badge>
          <Badge size="xs" radius="sm">
            {process}
          </Badge>
        </div>
      )}
      {negativePrompt && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <Text className="font-semibold">Negative prompt</Text>
            <CopyButton value={negativePrompt}>
              {({ copy, copied, Icon, color }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy negative prompt'}>
                  <LegacyActionIcon onClick={copy} color={color}>
                    <Icon size={16} />
                  </LegacyActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </div>
          <LineClamp c="dimmed" className="text-sm">
            {negativePrompt}
          </LineClamp>
        </div>
      )}
      {hasSimpleMeta && (
        <>
          <Divider />
          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <Text className="font-semibold">Other metadata</Text>
            </div>
            <div className="flex flex-wrap gap-2">
              {simpleMeta.map(({ label, content }) => (
                <Badge key={label} classNames={{ label: 'flex gap-1 items-center leading-snug' }}>
                  <span>{label}:</span>
                  {content}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
